import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { money, num } from "../../lib/format";
import type { PartnerListItem, PartnerPayout, PartnersListResponse } from "../../../shared/types";
import { useRail } from "../../../shared/phone/media";
import { PhoneNav } from "../../../shared/phone/PhoneNav";
import { Chevron, Segmented } from "../../../shared/phone/Row";
import { ActionSheet } from "../../../shared/phone/Sheet";
import { Split } from "../../../shared/phone/Split";
import { Icon } from "../../components/ui/Icon";
import { PartnerScreen } from "./PartnerScreen";
import { dayLabel, Figures, Monogram } from "./parts";

type Tab = "active" | "pending";

const fullName = (item: PartnerListItem): string => `${item.partner.firstName} ${item.partner.lastName}`.trim();

/** A partner's money in one pill, in the pay run's words. */
function MoneyPill({ payout }: { payout: PartnerPayout }) {
  if (payout.state === "ready") return <span className="pn-pill acc">{money(payout.readyCents)} ready</span>;
  if (payout.state === "blocked") return <span className="pn-pill warn">On hold</span>;
  if (payout.state === "below_min") return <span className="pn-pill">{money(payout.owedCents)} waiting</span>;
  if (payout.lastPaidAt) return <span className="pn-pill good">Paid {dayLabel(payout.lastPaidAt)}</span>;
  return <span className="pn-pill">Nothing owed</span>;
}

/** The figures the Partners screen leads with. */
export interface PartnerTotals {
  activeCount: number;
  pendingCount: number;
  regs: number;
  sales: number;
  owedCents: number;
  readyCents: number;
}

/**
 * Partners on a phone or an iPad: search pinned under the title, what is owed leading to Pay partners,
 * Active and Pending as a segmented control, one row per partner. On an iPad the list sits beside the
 * partner picked (`?p=` in the address), and a tap shows them there.
 */
export function PartnersScreen({
  id,
  productName,
  data,
  totals,
  loading,
  notFound,
  err,
  tab,
  setTab,
  query,
  setQuery,
  filterRows,
  busyId,
  onApprove,
  onReject,
  onAdd,
  onChanged,
}: {
  id: string;
  productName: string | null;
  data: PartnersListResponse | null;
  totals: PartnerTotals | null;
  loading: boolean;
  notFound: boolean;
  err: string | null;
  tab: Tab;
  setTab: (t: Tab) => void;
  query: string;
  setQuery: (q: string) => void;
  filterRows: (rows: PartnerListItem[]) => PartnerListItem[];
  busyId: string | null;
  onApprove: (item: PartnerListItem) => void;
  onReject: (item: PartnerListItem) => void;
  onAdd: () => void;
  onChanged: () => void;
}) {
  const rail = useRail();
  const [params] = useSearchParams();
  const [rejecting, setRejecting] = useState<PartnerListItem | null>(null);
  const product = `/products/${encodeURIComponent(id)}`;
  const payrun = `${product}/payrun`;

  const actions = (
    <>
      <button type="button" className="pn-tbtn" onClick={onAdd} aria-label="Add partners">
        <Icon name="plus" />
      </button>
      <Link className="pn-tbtn" to="?program" aria-label="Program settings">
        <Icon name="gear" />
      </Link>
    </>
  );
  const empty = !!totals && totals.activeCount === 0 && totals.pendingCount === 0;
  const split = rail && !!data && !empty;
  const nav = <PhoneNav title="Partners" large={!split} back={{ to: product, label: productName ?? "Product" }} actions={actions} />;

  const picked = params.get("p");
  const selected = data?.active.find((x) => x.partner.id === picked)?.partner.id ?? data?.active[0]?.partner.id ?? null;

  const state = loading ? (
    <div className="pn-empty">Loading partners…</div>
  ) : notFound ? (
    <div className="pn-empty">
      <b>Product not found</b>
      This product does not exist, or it belongs to another account.
    </div>
  ) : err ? (
    <div className="pn-empty">
      <b>Couldn't load partners</b>
      {err}
    </div>
  ) : null;

  const rejectSheet = (
    <ActionSheet
      open={!!rejecting}
      onClose={() => setRejecting(null)}
      title={rejecting ? `Reject ${fullName(rejecting)}?` : undefined}
      message="They will not join the program."
      actions={[{ label: "Reject", danger: true, onSelect: () => rejecting && onReject(rejecting) }]}
    />
  );

  if (state || !data || !totals) {
    return (
      <div className="pn-screen">
        {nav}
        {state}
      </div>
    );
  }

  if (empty) {
    return (
      <div className="pn-screen">
        {nav}
        <div className="pn-empty pa-empty">
          <Icon name="handshake" />
          <b>No partners yet</b>
          Invite people to promote {productName ?? "your product"} with a link. You reward real customers, and Flashadz keeps the count.
        </div>
        <div className="pn-pad">
          <button type="button" className="pn-btn pri wide" onClick={onAdd}>
            Add partners
          </button>
        </div>
      </div>
    );
  }

  const rows = tab === "active" ? filterRows(data.active) : filterRows(data.pending);
  const list = (
    <div className="pn-stack tight">
      <div className="pa-pin">
        <label className="pn-search">
          <Icon name="search" />
          <input type="search" placeholder="Search partners" aria-label="Search partners" value={query} onChange={(e) => setQuery(e.target.value)} />
        </label>
      </div>
      <div className="pn-grp">
        <Link className="pn-row pa-owed" to={payrun}>
          <span className="m">
            <span className="s">Owed to partners</span>
            <span className="pa-big">{money(totals.owedCents)}</span>
            <span className="pa-rdy">{totals.readyCents > 0 ? `${money(totals.readyCents)} ready to pay` : "Nothing ready to pay yet"}</span>
          </span>
          <Chevron />
        </Link>
        <Figures
          items={[
            [num(totals.activeCount), "Active"],
            [num(totals.regs), "Registered"],
            [num(totals.sales), "Sales"],
          ]}
        />
      </div>
      <Segmented<Tab>
        label="Partners"
        value={tab}
        onChange={setTab}
        options={[
          { value: "active", label: `Active ${num(totals.activeCount)}` },
          { value: "pending", label: `Pending ${num(totals.pendingCount)}` },
        ]}
      />
      <div className="pn-grp">
        {rows.length === 0 ? (
          <div className="pn-row static">
            <span className="m">
              <span className="s">
                {query.trim()
                  ? "No one matches your search."
                  : tab === "active"
                    ? "No active partners yet."
                    : "No one is waiting for approval."}
              </span>
            </span>
          </div>
        ) : tab === "active" ? (
          rows.map((item) => {
            const p = item.partner;
            const why = item.payout.state === "blocked" || item.payout.state === "below_min" ? item.payout.reason : null;
            const on = split && p.id === selected;
            return (
              <Link
                key={p.id}
                className={on ? "pn-row pa-ptr on" : "pn-row pa-ptr"}
                to={split ? { search: `?p=${encodeURIComponent(p.id)}` } : `/partners/${encodeURIComponent(p.id)}`}
                replace={split}
                aria-current={on ? "true" : undefined}
              >
                <Monogram name={fullName(item)} />
                <span className="m">
                  <span className="t">{fullName(item)}</span>
                  {why ? <span className="s pa-why">{why}</span> : null}
                  <span className="s">/go?p={p.slug}</span>
                </span>
                <span className="pa-r">
                  <span className="pa-earn">{money(item.kpis.earnedCents)}</span>
                  <MoneyPill payout={item.payout} />
                </span>
              </Link>
            );
          })
        ) : (
          rows.map((item) => {
            const p = item.partner;
            const busy = busyId === p.id;
            return (
              <div className="pn-row static pa-pend" key={p.id}>
                <div className="pa-pend-top">
                  <Monogram name={fullName(item)} />
                  <span className="m">
                    <span className="t">{fullName(item)}</span>
                    <span className="s">{p.email}</span>
                    <span className="s">
                      Applied {dayLabel(p.appliedAt) || "recently"} · agreement {item.documents.agreement ? "signed" : "not signed yet"}
                    </span>
                  </span>
                </div>
                <div className="pn-btns pa-pend-b">
                  <button type="button" className="pn-btn sm" disabled={busy} onClick={() => setRejecting(item)}>
                    Reject
                  </button>
                  <button type="button" className="pn-btn pri sm" disabled={busy} onClick={() => onApprove(item)}>
                    Approve
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
  const foot =
    totals.readyCents > 0 ? (
      <div className="pn-foot">
        <Link className="pn-btn pri wide" to={payrun}>
          Pay partners, {money(totals.readyCents)} ready
        </Link>
      </div>
    ) : null;

  if (!split) {
    return (
      <div className="pn-screen">
        {nav}
        {list}
        {foot}
        {rejectSheet}
      </div>
    );
  }

  return (
    <div className="pn-screen pa-splitscreen">
      {nav}
      <Split
        showDetail={false}
        list={
          <div className="pn-screen">
            {list}
            {foot}
          </div>
        }
        detail={
          selected ? (
            <PartnerScreen id={selected} pane onChanged={onChanged} />
          ) : (
            <div className="pn-screen">
              <div className="pn-empty">
                <b>No active partners yet</b>
                Approve someone waiting, or add partners.
              </div>
            </div>
          )
        }
      />
      {rejectSheet}
    </div>
  );
}
